import React, { useEffect, useRef } from 'react';

/**
 * MagicCursor - A gentle sparkle trail cursor for Our LA Story
 * Creates a calm, magical yellow trail following the mouse
 */
function MagicCursor() {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const mouseRef = useRef({ x: 0, y: 0 });
  const animationRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Set canvas to full screen
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Particle class for gentle sparkles
    class Particle {
      constructor(x, y) {
        this.x = x + (Math.random() - 0.5) * 8;
        this.y = y + (Math.random() - 0.5) * 8;
        this.size = Math.random() * 4 + 2;
        this.speedX = (Math.random() - 0.5) * 0.5;
        this.speedY = (Math.random() - 0.5) * 0.5 - 0.3;
        this.life = 1;
        this.decay = Math.random() * 0.025 + 0.02;
        this.rotation = Math.random() * Math.PI * 2;
        this.rotationSpeed = (Math.random() - 0.5) * 0.05;

        // Yellow/gold color palette
        const colors = [
          '#ffd93d', // Bright yellow
          '#ffeb99', // Light yellow
          '#ffe066', // Golden yellow
          '#fff3b0', // Cream yellow
          '#ffc107', // Amber
        ];
        this.color = colors[Math.floor(Math.random() * colors.length)];

        // Mostly dots, occasional sparkle
        this.type = Math.random() < 0.8 ? 'dot' : 'sparkle';
      }

      drawSparkle(ctx) {
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);
        ctx.globalAlpha = this.life * 0.7;

        const size = this.size * 0.6;
        ctx.beginPath();
        for (let i = 0; i < 4; i++) {
          const angle = (i * Math.PI) / 2;
          const outerX = Math.cos(angle) * size;
          const outerY = Math.sin(angle) * size;
          const innerAngle = angle + Math.PI / 4;
          const innerX = Math.cos(innerAngle) * (size * 0.3);
          const innerY = Math.sin(innerAngle) * (size * 0.3);

          if (i === 0) {
            ctx.moveTo(outerX, outerY);
          } else {
            ctx.lineTo(outerX, outerY);
          }
          ctx.lineTo(innerX, innerY);
        }
        ctx.closePath();
        ctx.fillStyle = this.color;
        ctx.fill();

        ctx.restore();
      }

      drawDot(ctx) {
        ctx.save();
        ctx.globalAlpha = this.life * 0.6;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size * 0.4, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.fill();
        ctx.restore();
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        this.life -= this.decay;
        this.rotation += this.rotationSpeed;
        this.size *= 0.99;
      }

      draw(ctx) {
        if (this.type === 'sparkle') {
          this.drawSparkle(ctx);
        } else {
          this.drawDot(ctx);
        }
      }
    }

    // Cursor dot (main cursor indicator)
    const drawCursor = () => {
      const { x, y } = mouseRef.current;
      if (x === 0 && y === 0) return;

      // Soft outer glow
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, 15);
      gradient.addColorStop(0, 'rgba(255, 217, 61, 0.25)');
      gradient.addColorStop(1, 'rgba(255, 217, 61, 0)');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(x, y, 15, 0, Math.PI * 2);
      ctx.fill();

      // Inner dot - yellow
      ctx.fillStyle = '#ffd93d';
      ctx.beginPath();
      ctx.arc(x, y, 5, 0, Math.PI * 2);
      ctx.fill();

      // Tiny white center highlight
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.arc(x - 1, y - 1, 1.5, 0, Math.PI * 2);
      ctx.fill();
    };

    // Animation loop
    let lastSpawn = 0;
    const animate = (timestamp) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Spawn new particles (slower rate for calmer effect)
      if (timestamp - lastSpawn > 60) {
        const { x, y } = mouseRef.current;
        if (x > 0 && y > 0) {
          particlesRef.current.push(new Particle(x, y));
        }
        lastSpawn = timestamp;
      }

      // Update and draw particles
      particlesRef.current = particlesRef.current.filter(p => p.life > 0);
      particlesRef.current.forEach(p => {
        p.update();
        p.draw(ctx);
      });

      // Draw main cursor
      drawCursor();

      animationRef.current = requestAnimationFrame(animate);
    };

    // Mouse move handler
    const handleMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };

    // Touch support
    const handleTouchMove = (e) => {
      if (e.touches.length > 0) {
        mouseRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchmove', handleTouchMove);

    // Start animation
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 10000,
      }}
    />
  );
}

export default MagicCursor;
